import React, { useMemo, useState } from "react";
import {
  Box,
  Container,
  Typography,
  Tabs,
  Tab,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { AnimatePresence, motion } from "framer-motion";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import ChurchOutlinedIcon from "@mui/icons-material/ChurchOutlined";
import SecurityOutlinedIcon from "@mui/icons-material/SecurityOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";

type GDPRSection = {
  label: string;
  shortLabel: string;
  title: string;
  icon: React.ReactNode;
  paragraphs: string[];
  bulletIntro?: string;
  bullets: string[];
  closingParagraphs?: string[];
};

const GDPR_SECTIONS: Record<string, GDPRSection> = {
  individualRights: {
    label: "Your Rights",
    shortLabel: "Your Rights",
    title: "Your Rights",
    icon: <PersonOutlineIcon />,
    paragraphs: [
      "If an organisation stores information about you in Pathways, you have rights under UK data-protection law.",
    ],
    bulletIntro: "You may have the right to:",
    bullets: [
      "Know what information is held about you and why.",
      "Request a copy of your information.",
      "Correct information that is wrong or incomplete.",
      "Ask for information to be deleted or restricted.",
      "Object to certain uses of your information.",
      "Complain if you believe your information has been handled unfairly or unlawfully.",
    ],
    closingParagraphs: [
      "You should normally contact the church or organisation that collected your information. Pathways will help them respond where necessary.",
      "Churches may sometimes hold sensitive information, such as religious beliefs, pastoral notes, health information or prayer requests. This information must be treated with extra care and only collected where there is a lawful reason.",
    ],
  },

  organisationResponsibilities: {
    label: "Churches and Organisations",
    shortLabel: "Organisations",
    title: "Responsibilities of Churches and Organisations",
    icon: <ChurchOutlinedIcon />,
    paragraphs: [
      "Churches and organisations using Pathways are responsible for deciding what information they collect, why they need it and who can access it.",
    ],
    bulletIntro: "They must:",
    bullets: [
      "Only collect information for a clear and lawful reason.",
      "Explain to people how their information will be used.",
      "Collect only information that is necessary.",
      "Keep information accurate and up to date.",
      "Avoid recording unnecessary, intrusive or inappropriate details.",
      "Protect sensitive information.",
      "Limit access to authorised staff and volunteers.",
      "Remove access when someone leaves or changes role.",
      "Respond to requests from individuals about their information.",
      "Delete information when it is no longer needed.",
      "Report any suspected data breach or unauthorised access.",
    ],
    closingParagraphs: [
      "Using Pathways does not automatically make data collection lawful. Each organisation remains responsible for following UK data-protection law.",
    ],
  },

  pathwaysResponsibilities: {
    label: "Pathways’ Responsibilities",
    shortLabel: "Pathways",
    title: "Pathways’ Responsibilities",
    icon: <SecurityOutlinedIcon />,
    paragraphs: [
      "Pathways is responsible for protecting the information stored within the platform and processing it only for authorised purposes.",
    ],
    bulletIntro: "Pathways commits to:",
    bullets: [
      "Process information only on behalf of the customer organisation.",
      "Keep personal information confidential.",
      "Use appropriate security measures to protect stored information.",
      "Restrict access to authorised personnel.",
      "Maintain backups and recovery procedures.",
      "Help organisations respond to data requests.",
      "Notify affected organisations if Pathways becomes aware of a data breach.",
      "Use trusted service providers under appropriate data-protection agreements.",
      "Protect information transferred outside the UK where applicable.",
      "Securely delete or return customer information when services end, subject to legal and backup requirements.",
    ],
    closingParagraphs: [
      "No online system can guarantee complete security. Pathways will regularly review and improve its security and data-protection practices.",
    ],
  },
};

const GDPRStatement = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [activeTab, setActiveTab] = useState(0);

  const sectionKeys = useMemo(() => Object.keys(GDPR_SECTIONS), []);
  const activeKey = sectionKeys[
    activeTab
  ] as keyof typeof GDPR_SECTIONS;
  const activeSection = GDPR_SECTIONS[activeKey];

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: "linear-gradient(135deg, #0f172a, #1e293b)",
        py: { xs: 5, md: 9 },
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* Background decoration */}
      <Box
        sx={{
          position: "absolute",
          top: -180,
          right: -180,
          width: 420,
          height: 420,
          borderRadius: "50%",
          background: "rgba(74, 222, 128, 0.12)",
          filter: "blur(20px)",
          pointerEvents: "none",
        }}
      />

      <Box
        sx={{
          position: "absolute",
          bottom: -220,
          left: -180,
          width: 480,
          height: 480,
          borderRadius: "50%",
          background: "rgba(34, 197, 94, 0.08)",
          filter: "blur(25px)",
          pointerEvents: "none",
        }}
      />

      <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
        {/* Header */}
        <Box
          component={motion.div}
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          sx={{
            textAlign: "center",
            maxWidth: 850,
            mx: "auto",
            mb: { xs: 5, md: 7 },
          }}
        >
          <Typography
            variant="overline"
            sx={{
              display: "inline-block",
              color: "#4ade80",
              fontWeight: 900,
              letterSpacing: "0.16em",
              mb: 1.5,
            }}
          >
            Privacy and Data Protection
          </Typography>

          <Typography
            component="h1"
            sx={{
              color: "#fcfaf6",
              fontWeight: 900,
              fontSize: {
                xs: "2.1rem",
                sm: "2.7rem",
                md: "3.3rem",
              },
              lineHeight: 1.1,
              mb: 3,
            }}
          >
            Pathways GDPR Statement
          </Typography>
        </Box>

        <Box sx={{ mb:5}}>
          <Typography
            sx={{
              color: "rgba(255,255,255,0.88)",
              lineHeight: 1.8,
              fontSize: { xs: "0.98rem", md: "1.05rem" },
            }}
          >
            Pathways helps churches and organisations store and manage
            information about people, households, events and community
            engagement.
          </Typography>


          <Typography
            sx={{
              color: "rgba(255,255,255,0.88)",
              lineHeight: 1.8,
              fontSize: { xs: "0.98rem", md: "1.05rem" },
            }}
          >
            In most cases, the church or organisation decides what information
            is collected and why. This makes them the{" "}
            <Box
              component="span"
              sx={{ color: "#4ade80", fontWeight: 800 }}
            >
              data controller
            </Box>
            .
          </Typography>

          <Typography
            sx={{
              color: "rgba(255,255,255,0.88)",
              lineHeight: 1.8,
              fontSize: { xs: "0.98rem", md: "1.05rem" },
              mt: 1.5,
            }}
          >
            Pathways stores and processes the information on their behalf,
            making Pathways the{" "}
            <Box
              component="span"
              sx={{ color: "#4ade80", fontWeight: 800 }}
            >
              data processor
            </Box>
            .
          </Typography>
        </Box>

        <Box
          sx={{
            display: "flex",
            flexDirection: isMobile ? "column" : "row",
            alignItems: "flex-start",
            gap: 4,
          }}
        >
          {/* Section navigation */}
          <Tabs
            orientation={isMobile ? "horizontal" : "vertical"}
            variant="scrollable"
            scrollButtons="auto"
            value={activeTab}
            onChange={(_, newValue) => setActiveTab(newValue)}
            aria-label="GDPR statement sections"
            sx={{
              width: isMobile ? "100%" : 290,
              minWidth: isMobile ? "100%" : 290,
              backgroundColor: "rgba(30, 41, 59, 0.92)",
              borderRadius: 3,
              p: 1,
              boxShadow: "0 4px 16px rgba(0,0,0,0.15)",
              border: "1px solid rgba(255,255,255,0.08)",

              "& .MuiTabs-indicator": {
                display: "none",
              },

              "& .MuiTabs-flexContainer": {
                gap: 1,
                justifyContent: "flex-start",
                alignItems: "flex-start",
              },

              "& .MuiTab-root": {
                minHeight: isMobile ? 40 : 58,

                // Left-align the icon and text inside every tab
                alignItems: "flex-start",
                justifyContent: "flex-start",
                textAlign: "left",

                // Make vertical tabs fill the sidebar width
                width: isMobile ? "auto" : "100%",
                maxWidth: "none",

                textTransform: "none",
                fontWeight: 700,
                color: "#fcfaf6",
                borderRadius: 2,
                px: 2,
                py: 1.5,
                transition: "all 0.25s ease",
                whiteSpace: isMobile ? "nowrap" : "normal",

                "&.Mui-selected": {
                  backgroundColor: "#4ade80",
                  color: "#0f172a",
                  boxShadow: "0 3px 10px rgba(0,0,0,0.2)",
                },

                "&:hover": {
                  backgroundColor: "rgba(74,222,128,0.1)",
                },

                "&.Mui-selected:hover": {
                  backgroundColor: "#4ade80",
                },
              },

              "& .MuiTab-iconWrapper": {
                mb: isMobile ? 0.5 : 0,
                mr: isMobile ? 0 : 1.25,
              },
            }}
          >
            {sectionKeys.map((key) => {
              const section =
                GDPR_SECTIONS[key as keyof typeof GDPR_SECTIONS];

              return (
                <Tab
                  key={key}
                  icon={section.icon as React.ReactElement}
                  iconPosition={isMobile ? "top" : "start"}
                  label={isMobile ? section.shortLabel : section.label}
                />
              );
            })}
          </Tabs>

          {/* Active section */}
          <Box sx={{ flex: 1, width: "100%" }}>
            <AnimatePresence mode="wait">
              <motion.div
                key={activeKey}
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -18 }}
                transition={{ duration: 0.4 }}
              >
                <Box
                  sx={{
                    borderRadius: 5,
                    p: { xs: 3, sm: 4, md: 5 },
                    background: "rgba(255,255,255,0.06)",
                    border: "1px solid rgba(255,255,255,0.14)",
                    backdropFilter: "blur(10px)",
                    boxShadow: "0 20px 60px rgba(0,0,0,0.35)",
                    position: "relative",
                    overflow: "hidden",
                  }}
                >
                  {/* Corner glow */}
                  <Box
                    sx={{
                      position: "absolute",
                      right: -90,
                      top: -90,
                      width: 240,
                      height: 240,
                      borderRadius: "50%",
                      background: "rgba(74, 222, 128, 0.28)",
                      filter: "blur(4px)",
                      pointerEvents: "none",
                    }}
                  />

                  <Box sx={{ position: "relative", zIndex: 1 }}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1.5,
                        mb: 3,
                      }}
                    >
                      <Box
                        sx={{
                          width: 48,
                          height: 48,
                          borderRadius: 2.5,
                          backgroundColor: "#4ade80",
                          color: "#0f172a",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                          "& svg": {
                            fontSize: 28,
                          },
                        }}
                      >
                        {activeSection.icon}
                      </Box>

                      <Typography
                        component="h2"
                        sx={{
                          color: "#ffffff",
                          fontWeight: 900,
                          fontSize: {
                            xs: "1.45rem",
                            sm: "1.7rem",
                            md: "2rem",
                          },
                          lineHeight: 1.2,
                        }}
                      >
                        {activeSection.title}
                      </Typography>
                    </Box>

                    {activeSection.paragraphs.map((paragraph) => (
                      <Typography
                        key={paragraph}
                        sx={{
                          color: "rgba(255,255,255,0.86)",
                          lineHeight: 1.8,
                          mb: 2,
                        }}
                      >
                        {paragraph}
                      </Typography>
                    ))}

                    {activeSection.bulletIntro && (
                      <Typography
                        sx={{
                          color: "#ffffff",
                          fontWeight: 800,
                          mt: 3,
                          mb: 2,
                        }}
                      >
                        {activeSection.bulletIntro}
                      </Typography>
                    )}

                    <Box
                      component="ul"
                      sx={{
                        listStyle: "none",
                        p: 0,
                        m: 0,
                        display: "flex",
                        flexDirection: "column",
                        gap: 1.4,
                      }}
                    >
                      {activeSection.bullets.map((bullet, index) => (
                        <Box
                          component={motion.li}
                          key={bullet}
                          initial={{ opacity: 0, x: 12 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{
                            duration: 0.3,
                            delay: 0.05 * index,
                          }}
                          sx={{
                            display: "flex",
                            alignItems: "flex-start",
                            gap: 1.5,
                            p: 1.5,
                            borderRadius: 2,
                            backgroundColor: "rgba(15, 23, 42, 0.34)",
                            border: "1px solid rgba(255,255,255,0.06)",
                          }}
                        >
                          <CheckCircleOutlineIcon
                            sx={{
                              color: "#4ade80",
                              mt: "2px",
                              fontSize: 21,
                              flexShrink: 0,
                            }}
                          />

                          <Typography
                            sx={{
                              color: "rgba(255,255,255,0.86)",
                              lineHeight: 1.65,
                            }}
                          >
                            {bullet}
                          </Typography>
                        </Box>
                      ))}
                    </Box>

                    {activeSection.closingParagraphs?.map(
                      (paragraph, index) => (
                        <Typography
                          key={paragraph}
                          sx={{
                            color: "rgba(255,255,255,0.86)",
                            lineHeight: 1.8,
                            mt: index === 0 ? 3 : 2,
                          }}
                        >
                          {paragraph}
                        </Typography>
                      )
                    )}
                  </Box>
                </Box>
              </motion.div>
            </AnimatePresence>
          </Box>
        </Box>

        {/* Footer note */}
        <Typography
          sx={{
            color: "rgba(255,255,255,0.48)",
            textAlign: "center",
            fontSize: "0.85rem",
            lineHeight: 1.7,
            mt: 5,
          }}
        >
          This statement explains the general responsibilities of Pathways and
          organisations using the platform. Organisations should maintain their
          own privacy notices and data-protection procedures.
        </Typography>
      </Container>
    </Box>
  );
};

export default GDPRStatement;